import { graphqlGLClient } from '../../api/gqlaxy/gql/queries/graphQLClient'
import {
  ClientEventName,
  ClientEventSource,
  CreateClientEventMutation,
  CreateClientEventMutationVariables
} from '../../api/gqlaxy/gql/generated/graphql'
import { CreateClientEventDocument } from '../../api/gqlaxy/gql/generated/graphql-hooks'
import * as Sentry from '@sentry/browser'
import { PATH_HELPERS } from '../../constants/navigation'

export const UTM_CONTENT = {
  VIEW_YOUR_LISTING: 'view_your_listing'
}

/**
 * Fires a client event when a user opts in to receive emails by accepting an invite.
 * This is called during the auth callback flow when the user has acceptInvite=true in the URL.
 */
export const fireEmailOptInEvent = (): void => {
  const client = graphqlGLClient()

  try {
    client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
      clientEventInput: {
        eventName: ClientEventName.EmailOptIn,
        eventSource: ClientEventSource.ForYou
      }
    })
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'clientEvent',
      message: 'Error firing email opt-in client event',
      level: 'error'
    })
    Sentry.captureException(error)
  }
}

/**
 * Fires a client event when a DBO client self upgrades to be a paid client.
 * This is called during the home creation flow when selfUpgrade=true.
 * @param homeId - The home ID to associate with the event
 */
export const fireDboClientTypeChangeEvent = (homeId: string): void => {
  const client = graphqlGLClient()

  try {
    client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
      clientEventInput: {
        eventName: ClientEventName.DboClientTypeChange,
        eventSource: ClientEventSource.Digest,
        homeId,
        properties: {
          old_type: 'prospect',
          new_type: 'paid'
        }
      }
    })
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'clientEvent',
      message: 'Error firing DBO client type change event',
      level: 'error'
    })
    Sentry.captureException(error)
  }
}

/**
 * Fires a client event based on the utm_content query parameter.
 * This is called during the auth callback flow to track user engagement from email links.
 *
 * @param utmContent - The utm_content query parameter value that is defined and passed in from mikasa dynamic email data templates like we do here for example: app/services/api_email_delivery/client_emails/listing_alert/dynamic_data/v1.rb (Mikasa)
 */
export const fireClientEventFromUtmContent = (utmContent: string, redirectUrl: string): void => {
  const client = graphqlGLClient()

  try {
    switch (utmContent) {
      case UTM_CONTENT.VIEW_YOUR_LISTING: {
        client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
          clientEventInput: {
            eventName: ClientEventName.ViewedTheirOwnListing,
            eventSource: ClientEventSource.ListingDetailed,
            listingId: redirectUrl.split('/').pop(), // grabs the listing id from the redirect url which is formatted like this when utm_content is 'view_your_listing': /home-search/{customerProfileId/listing/{listingId}
            properties: {
              activity_source: 'email'
            }
          }
        })
        break
      }
      default:
        // No-op we don't handle other utm_content values yet
        break
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'clientEvent',
      message: 'Error firing client event from utm_content',
      level: 'error',
      data: {
        utmContent
      }
    })
    Sentry.captureException(error)
  }
}
